import { DataSetConfig, DataSetContext, DataSetScope, DataSetReference, AnalyticsDataPoint, QueryFilter, AnalyticsVisualization, AnalyticsConfig } from '../types/analytics.types';
import { IAnalyticsService } from '../types/analytics.interfaces';
import { MockDataGenerator } from './mock-data.generator';
import { DateConstantsService } from './date-constants.service';

// Debug logging utility - only logs in development mode
const DEBUG = process.env.NODE_ENV === 'development';
const debugLog = (message: string, ...args: any[]) => {
  if (DEBUG) {
    //   console.log(message, ...args);
  }
};

/**
 * Data Set Manager - Multi-level data management for analytics dashboards
 * 
 * Provides efficient data loading with hierarchical resolution:
 * component → tab → dashboard → individual API calls
 * 
 * Key Features:
 * - Smart caching with TTL
 * - Hierarchical data set resolution  
 * - Client-side filtering and aggregation
 * - Performance monitoring
 */
export class DataSetManager {
  private dataSets: Map<string, DataSetConfig> = new Map();
  private dataCache: Map<string, { data: AnalyticsDataPoint[]; timestamp: number; ttl: number }> = new Map();
  private loadingPromises: Map<string, Promise<AnalyticsDataPoint[]>> = new Map();
  private mockDataGenerator: MockDataGenerator;

  // NEW: Dashboard-level configuration storage
  private dashboardConfigs: Map<string, AnalyticsConfig> = new Map();
  private forceLiveData: boolean = false; // Override mock data generation

  // PERFORMANCE: Date parsing cache to avoid re-parsing same date strings
  private dateParseCach: Map<string, Date> = new Map();

  // PERFORMANCE: Pre-filtered data cache to avoid re-filtering for each visualization
  private preFilteredCache: Map<string, { data: AnalyticsDataPoint[]; filterKey: string }> = new Map();

  constructor(private analyticsService: IAnalyticsService) {
    this.mockDataGenerator = new MockDataGenerator();
  }

  /**
   * Set whether to force live data calls (override mock data)
   */
  setForceLiveData(forceLiveData: boolean): void {
    this.forceLiveData = forceLiveData;
    debugLog(`🔧 [DataSetManager] Set forceLiveData: ${forceLiveData}`);

    // Clear cache and remove mock datasets when switching to live data
    if (forceLiveData) {
      debugLog(`🧹 [DataSetManager] Clearing ALL cache and mock datasets to force server calls`);
      this.dataCache.clear();

      // Remove all datasets with mockDataConfig to prevent mock data serving
      const mockDatasetIds: string[] = [];
      this.dataSets.forEach((dataset, id) => {
        if (dataset.mockDataConfig) {
          mockDatasetIds.push(id);
        }
      });

      mockDatasetIds.forEach(id => {
        debugLog(`🗑️ [DataSetManager] Removing mock dataset: ${id}`);
        this.dataSets.delete(id);
      });
    }
  }

  /**
   * Register data sets from configuration
   */
  registerDataSets(context: DataSetContext): void {
    // Register dashboard-level dataset
    if (context.dashboard) {
      this.dataSets.set(context.dashboard.id, context.dashboard);
    }

    // Register tab-level datasets
    if (context.tabs) {
      Object.values(context.tabs).forEach(dataSet => {
        this.dataSets.set(dataSet.id, dataSet);
      });
    }

    // Register component-level datasets
    if (context.components) {
      Object.values(context.components).forEach(dataSet => {
        this.dataSets.set(dataSet.id, dataSet);
      });
    }

  }

  /**
   * Register dashboard configuration (NEW - for unified mock data and static datasets)
   */
  registerDashboardConfig(dashboardConfig: AnalyticsConfig): void {
    this.dashboardConfigs.set(dashboardConfig.id, dashboardConfig);


    // 🚀 RESTORED: Generate cache key from timestamps for cache invalidation
    let configKey = '';
    const configWithTimestamps = dashboardConfig as any;

    // Check for preview mode cache key first (from analytics.dashboard.resource.tsx)
    if (configWithTimestamps._cacheKey) {
      configKey = configWithTimestamps._cacheKey;
    } else if (configWithTimestamps.updatedAt) {
      const timestamp = new Date(configWithTimestamps.updatedAt).getTime();
      configKey = `-config-${timestamp}`;
    } else if (configWithTimestamps.createdAt) {
      const timestamp = new Date(configWithTimestamps.createdAt).getTime();
      configKey = `-config-${timestamp}`;
    }

    debugLog(`🔧 [DataSetManager] Registering dashboard config: ${dashboardConfig.id}, cache key: ${configKey}`);

    // 🚀 NEW: Handle static datasets (when dataSource: "datasets" with data arrays)
    if (dashboardConfig.dataSource === 'datasets' && dashboardConfig.dataSets) {
      Object.entries(dashboardConfig.dataSets).forEach(([key, dataSetOrConfig]) => {
        // Check if this is a static dataset (has a data array)
        if (dataSetOrConfig && typeof dataSetOrConfig === 'object' && 'data' in dataSetOrConfig && Array.isArray(dataSetOrConfig.data)) {

          // Create a DataSetConfig for this static dataset
          const staticDataSetConfig: DataSetConfig = {
            id: key,
            name: (dataSetOrConfig as any).name || `Static Dataset ${key}`,
            description: (dataSetOrConfig as any).description || `Static dataset ${key}`,
            scope: 'dashboard',
            source: {
              endpoint: `/analytics/datasets/static/${key}`,
              baseFilters: [],
              cacheKey: `static-${key}-v1` // Stable cache key without timestamp
            },
            refreshStrategy: 'never', // Static data never refreshes
            staticData: dataSetOrConfig.data // Store the static data
          };

          this.dataSets.set(key, staticDataSetConfig);

          // Pre-cache the static data
          const cacheKey = this.getCacheKey(key, {});
          this.dataCache.set(cacheKey, {
            data: dataSetOrConfig.data,
            timestamp: Date.now(),
            ttl: Number.MAX_SAFE_INTEGER // Never expires
          });

        }
      });
    }

    // Create virtual dataset if dashboard has mock data config (regardless of dataSource for flexibility)
    if (dashboardConfig.mockDataConfig) {
      // Determine mode suffix to prevent cache collisions between mock and live data
      // Preview mode (design mode in admin portal) should use different IDs than live mode
      const isPreviewMode = (dashboardConfig as any)._previewMode === true;
      const modeSuffix = isPreviewMode ? '-preview' : (this.forceLiveData ? '-live' : '-mock');

      // Use cache-busted dataset ID with mode differentiation for proper cache isolation
      const virtualDataSetId = `${dashboardConfig.id}${configKey}${modeSuffix}-dataset`;
      // Clean ID also includes mode to prevent collisions - visualizations will reference this
      const cleanDataSetId = `${dashboardConfig.id}${modeSuffix}-dataset`;

      const virtualDataSet: DataSetConfig = {
        id: virtualDataSetId,
        name: `${dashboardConfig.name} Dataset`,
        description: `Generated dataset for ${dashboardConfig.name}`,
        scope: 'dashboard',
        source: {
          endpoint: `/analytics/datasets/${dashboardConfig.id}-dataset`, // Clean endpoint for API routing
          baseFilters: [],
          // Include mode in cache key to prevent mock/live collisions
          cacheKey: `${dashboardConfig.id}${configKey}${modeSuffix}-unified-v3` // Cache-busted key with mode differentiation
        },
        refreshStrategy: 'on_load',
        mockDataConfig: dashboardConfig.mockDataConfig
      };

      // ✅ Register virtual dataset under mode-specific IDs to prevent cache collisions
      // CRITICAL: Use mode-specific IDs to ensure preview mode (design) doesn't collide with live mode
      this.dataSets.set(virtualDataSet.id, virtualDataSet); // Timestamped ID with mode for internal tracking
      this.dataSets.set(cleanDataSetId, virtualDataSet);    // Clean ID with mode for visualization lookups

      // 🚀 Clear any existing cache for this dataset to force fresh generation
      this.clearCache(virtualDataSet.id);
      this.clearCache(cleanDataSetId);

      // ⚠️ CRITICAL: Also register legacy clean ID (without mode suffix) for backward compatibility
      // Filter generation and other components may look up datasets using the clean ID without mode suffix
      const legacyCleanId = `${dashboardConfig.id}-dataset`;
      this.dataSets.set(legacyCleanId, virtualDataSet); // Register legacy ID for filter generation lookup

      // Clear any existing cache for legacy ID to prevent stale data
      this.clearCache(legacyCleanId);

      debugLog(`🔧 [DataSetManager] Registered dataset under mode-specific IDs (mode: ${modeSuffix}):`);
      debugLog(`  - Timestamped: ${virtualDataSet.id}`);
      debugLog(`  - Clean with mode: ${cleanDataSetId}`);
      debugLog(`  - Legacy clean ID: ${legacyCleanId} (for filter generation compatibility)`);
    } else if (dashboardConfig.dataSource === 'server' || dashboardConfig.dataSource === 'hybrid') {
      // For server/hybrid mode without mockDataConfig, create a dataset entry for filter generation
      // This allows dynamic filter options to be generated from server data
      const isPreviewMode = (dashboardConfig as any)._previewMode === true;
      const modeSuffix = isPreviewMode ? '-preview' : '-server';
      const cleanDataSetId = `${dashboardConfig.id}${modeSuffix}-dataset`;
      const legacyCleanId = `${dashboardConfig.id}-dataset`;

      const serverDataSet: DataSetConfig = {
        id: cleanDataSetId,
        name: `${dashboardConfig.name} Dataset`,
        description: `Server dataset for ${dashboardConfig.name}`,
        scope: 'dashboard',
        source: {
          endpoint: `/analytics/datasets/${dashboardConfig.id}-dataset`, // Backend API endpoint
          baseFilters: [],
          cacheKey: `${dashboardConfig.id}${modeSuffix}-server-v1`
        },
        refreshStrategy: 'on_load'
      };

      // Register both mode-specific and legacy IDs for compatibility
      this.dataSets.set(cleanDataSetId, serverDataSet);
      this.dataSets.set(legacyCleanId, serverDataSet);

      debugLog(`🔧 [DataSetManager] Registered server dataset for filter generation:`);
      debugLog(`  - Clean with mode: ${cleanDataSetId}`);
      debugLog(`  - Legacy clean ID: ${legacyCleanId} (for filter generation compatibility)`);
    }
  }

  /**
   * Get data for a visualization using hierarchical data set resolution
   */
  async getVisualizationData(
    visualizationId: string,
    dataSetRef?: DataSetReference,
    filters: Record<string, any> = {},
    tabId?: string,
    dashboardDataSetId?: string,
    visualization?: AnalyticsVisualization,
    requiredFields?: string[]
  ): Promise<AnalyticsDataPoint[]> {
    // Check conditional visibility first
    if (visualization && !this.shouldShowVisualization(visualization, filters)) {
      return [];
    }

    // Step 1: Try component-level dataset
    if (dataSetRef?.dataSetId) {
      // Find dashboard config for ID resolution
      const dashboardConfig = dashboardDataSetId ?
        Array.from(this.dashboardConfigs.values()).find(c => dashboardDataSetId.includes(c.id)) :
        undefined;
      const resolvedId = this.resolveDataSetId(dataSetRef.dataSetId, dashboardConfig);
      const componentData = await this.loadDataSet(resolvedId, filters);

      if (componentData.length > 0) {
        const filteredData = this.applyVisualizationFilters(componentData, filters, visualization, tabId, requiredFields);
        return filteredData;
      }
    }

    // Step 2: Try tab-level dataset  
    if (tabId && dataSetRef?.scope !== 'component') {
      const tabDataSetId = `${tabId}-dataset`;
      if (this.dataSets.has(tabDataSetId)) {
        const tabData = await this.loadDataSet(tabDataSetId, filters);
        if (tabData.length > 0) {
          const filtered = this.applyVisualizationFilters(tabData, filters, visualization, tabId, requiredFields);
          if (visualization?.type === 'kpi') {
            console.log(`📊 [DataSetManager] KPI '${visualizationId}' using TAB dataset → ${filtered.length} records after filters`);
          }
          return filtered;
        }
      }
    }

    // Step 3: Try dashboard-level dataset (already mode-specific from SmartDataManager)
    if (dashboardDataSetId && dataSetRef?.scope !== 'component' && dataSetRef?.scope !== 'tab') {
      debugLog(`🔍 [DataSetManager] Step 3: Trying dashboard dataset ${dashboardDataSetId}`);
      const dashboardData = await this.loadDataSet(dashboardDataSetId, filters);
      debugLog(`🔍 [DataSetManager] Step 3: Dashboard dataset returned ${dashboardData.length} records`);
      if (dashboardData.length > 0) {
        const filtered = this.applyVisualizationFilters(dashboardData, filters, visualization, tabId, requiredFields);
        if (visualization?.type === 'kpi') {
          console.log(`📊 [DataSetManager] KPI '${visualizationId}' using DASHBOARD dataset → ${filtered.length} records after filters`);
        }
        return filtered;
      }
    }

    // Step 4: Fallback based on mode
    const fallbackMode = dataSetRef?.fallbackMode || 'empty';
    switch (fallbackMode) {
      case 'empty':
        return [];
      case 'error':
        throw new Error(`No dataset available for visualization ${visualizationId}`);
      case 'api':
      default:
        // Return empty for now - would integrate with existing SmartDataManager logic
        return [];
    }
  }

  /**
   * Resolve dataset ID to mode-specific ID if needed
   * This ensures visualizations referencing legacy clean IDs get the correct mode-specific dataset
   */
  private resolveDataSetId(dataSetId: string, dashboardConfig?: AnalyticsConfig): string {
    // If ID already has mode suffix, return as-is
    if (dataSetId.includes('-preview-') || dataSetId.includes('-mock-') || dataSetId.includes('-live-')) {
      return dataSetId;
    }

    // If this is a dashboard-level dataset (ends with -dataset), try to resolve to mode-specific version
    if (dataSetId.endsWith('-dataset')) {
      // Extract dashboard ID from dataset ID (e.g., "dashboard-id-dataset" -> "dashboard-id")
      const baseId = dataSetId.replace('-dataset', '');

      // Find matching dashboard config
      const matchingConfig = dashboardConfig || Array.from(this.dashboardConfigs.values())
        .find(config => dataSetId.includes(config.id));

      if (matchingConfig) {
        const isPreviewMode = (matchingConfig as any)._previewMode === true;
        const modeSuffix = isPreviewMode ? '-preview' : (this.forceLiveData ? '-live' : '-mock');
        const modeSpecificId = `${baseId}${modeSuffix}-dataset`;

        // Check if mode-specific dataset exists
        if (this.dataSets.has(modeSpecificId)) {
          return modeSpecificId;
        }
      }
    }

    // Return original ID if no mode-specific version exists
    return dataSetId;
  }

  /**
   * Load a specific dataset with optimized mock data caching
   */
  async loadDataSet(dataSetId: string, filters: Record<string, any> = {}): Promise<AnalyticsDataPoint[]> {
    const loadStart = performance.now();

    // Resolve to mode-specific ID if needed
    const dashboardConfig = this.dashboardConfigs.get(dataSetId.split('-')[0]) ||
      Array.from(this.dashboardConfigs.values())[0];
    const resolvedDataSetId = this.resolveDataSetId(dataSetId, dashboardConfig);

    const dataSet = this.dataSets.get(resolvedDataSetId);
    if (!dataSet) {
      console.warn(`⚠️ [DataSetManager] Dataset not found: ${dataSetId} (resolved: ${resolvedDataSetId})`);
      return [];
    }

    // PERFORMANCE: Pre-compute timestamps on data rows for faster filtering
    const enhanceWithTimestamps = (data: AnalyticsDataPoint[]): AnalyticsDataPoint[] => {
      return data.map(row => {
        if (row.date && !(row as any)._timestamp) {
          const dateVal: any = row.date;
          if (typeof dateVal === 'string') {
            const s = dateVal.includes('T') ? dateVal.split('T')[0] : dateVal;
            const parts = s.split('-').map(Number);
            if (parts.length === 3 && parts.every(n => !isNaN(n))) {
              const [y, m, d] = parts;
              (row as any)._timestamp = new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
            }
          } else if (dateVal instanceof Date) {
            (row as any)._timestamp = new Date(dateVal.getFullYear(), dateVal.getMonth(), dateVal.getDate(), 0, 0, 0, 0).getTime();
          }
        }
        return row;
      });
    };

    // ⚡ PERFORMANCE OPTIMIZATION: Mock data doesn't need filters during generation
    // Generate once, cache for 30min, apply filters client-side (5-8ms vs 900ms regeneration)
    if (dataSet.mockDataConfig && !this.forceLiveData) {
      debugLog(`🔧 [DataSetManager] Using mock data for dataset: ${resolvedDataSetId} (original: ${dataSetId})`);
      const mockData = await this.loadMockDataSet(resolvedDataSetId, dataSet, filters, loadStart);
      return enhanceWithTimestamps(mockData);
    }

    // Force live data mode - make server calls even if mockDataConfig exists
    if (this.forceLiveData && dataSet.mockDataConfig) {
      debugLog(`🌐 [DataSetManager] forceLiveData enabled - making server call for dataset: ${resolvedDataSetId} (original: ${dataSetId})`);
      // Continue to API call logic below
    }

    // Original logic for API data (filters affect the API call)
    // Use resolvedDataSetId for cache keys to ensure mode-specific caching
    const cacheKey = this.getCacheKey(resolvedDataSetId, filters);
    const cached = this.dataCache.get(cacheKey);

    if (cached && this.isCacheValid(cached)) {
      const loadEnd = performance.now();
      const totalLoadTime = loadEnd - loadStart;
      //    debugLog(`⏱️ [Performance] Dataset '${resolvedDataSetId}' loaded from CACHE: ${cached.data.length} records in ${totalLoadTime.toFixed(2)}ms`);
      return cached.data;
    }

    // Check if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)!;
    }

    // Load data
    const loadingPromise = this.performDataSetLoad(dataSet, filters);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const data = await loadingPromise;

      console.log(`📊 [DataSetManager] CACHE: Dataset '${resolvedDataSetId}' (original: ${dataSetId}) loaded ${data.length} records from API`);

      // Log unique locations count for debugging
      if (data.length > 0) {
        const uniqueLocations = new Set(data.map(row => (row as any).location || (row as any).location_name || (row as any).LOCATION_NAME));
        console.log(`📊 [DataSetManager] CACHE: ${uniqueLocations.size} unique locations in loaded data`);
      }

      // PERFORMANCE: Enhance data with timestamps
      const enhancedData = enhanceWithTimestamps(data);

      // Cache the result using resolved ID to ensure mode-specific cache isolation
      const ttl = dataSet.clientTtlMs || 300000; // 5 min for API data
      this.dataCache.set(cacheKey, {
        data: enhancedData,
        timestamp: Date.now(),
        ttl
      });

      console.log(`📊 [DataSetManager] CACHE: Cached ${enhancedData.length} records with key: ${cacheKey}`);

      const loadEnd = performance.now();
      const totalLoadTime = loadEnd - loadStart;
      //   debugLog(`⏱️ [Performance] Dataset '${resolvedDataSetId}' loaded: ${data.length} records in ${totalLoadTime.toFixed(2)}ms`);

      return enhancedData;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Optimized loading for mock data: generate once, filter client-side
   */
  private async loadMockDataSet(
    dataSetId: string,
    dataSet: DataSetConfig,
    filters: Record<string, any>,
    loadStart: number
  ): Promise<AnalyticsDataPoint[]> {
    // 🚫 BLOCK mock data completely when in live data mode
    if (this.forceLiveData) {
      debugLog(`🚫 [DataSetManager] loadMockDataSet blocked - forceLiveData enabled for dataset: ${dataSetId}`);
      return []; // Return empty to force API calls
    }

    const baseCacheKey = this.getCacheKey(dataSetId, {}); // No filters in cache key for mock data
    const cached = this.dataCache.get(baseCacheKey);

    let baseData: AnalyticsDataPoint[];

    // Check if base mock data is cached
    if (cached && this.isCacheValid(cached)) {
      const cacheEnd = performance.now();
      const cacheTime = cacheEnd - loadStart;
      //   debugLog(`⏱️ [Performance] Mock dataset '${dataSetId}' loaded from CACHE: ${cached.data.length} records in ${cacheTime.toFixed(2)}ms`);
      baseData = cached.data;
    } else {
      // Generate base mock data (without filters)
      if (this.loadingPromises.has(baseCacheKey)) {
        baseData = await this.loadingPromises.get(baseCacheKey)!;
      } else {
        const loadingPromise = this.generateBaseMockData(dataSet);
        this.loadingPromises.set(baseCacheKey, loadingPromise);

        try {
          baseData = await loadingPromise;

          // Cache base mock data for longer (30 min)
          const ttl = dataSet.clientTtlMs || 1800000;
          this.dataCache.set(baseCacheKey, {
            data: baseData,
            timestamp: Date.now(),
            ttl
          });

          const loadEnd = performance.now();
          const totalLoadTime = loadEnd - loadStart;
          //    debugLog(`⏱️ [Performance] Mock dataset '${dataSetId}' GENERATED and cached: ${baseData.length} records in ${totalLoadTime.toFixed(2)}ms`);
        } finally {
          this.loadingPromises.delete(baseCacheKey);
        }
      }
    }

    // Return base cached data (no filtering here)
    // Each visualization will call applyVisualizationFilters() with visualization-specific logic
    return baseData;
  }

  /**
   * Generate base mock data without any filters
   */
  private async generateBaseMockData(dataSet: DataSetConfig): Promise<AnalyticsDataPoint[]> {
    const mockStart = performance.now();
    const mockData = await this.mockDataGenerator.generateMockData(dataSet, {}); // No filters!
    const mockEnd = performance.now();
    const mockTime = mockEnd - mockStart;

    //  debugLog(`⏱️ [Performance] Base mock data generation for '${dataSet.id}': ${mockData.length} records in ${mockTime.toFixed(2)}ms`);
    return mockData;
  }



  /**
   * Perform the actual data loading
   */
  private async performDataSetLoad(dataSet: DataSetConfig, filters: Record<string, any>): Promise<AnalyticsDataPoint[]> {

    // 🚀 First priority: Static data (for dataSource: "datasets")
    if (dataSet.staticData && Array.isArray(dataSet.staticData)) {
      return dataSet.staticData;
    }

    // Combine base filters with additional filters
    const allFilters = [...dataSet.source.baseFilters];

    // Add filters from the current request (like date ranges)
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Convert filter format as needed
        if (key.includes('date') && value && typeof value === 'object' && 'start' in value) {
          allFilters.push({
            field: 'date',
            operator: 'gte',
            value: value.start
          });
          if (value.end) {
            allFilters.push({
              field: 'date',
              operator: 'lte',
              value: value.end
            });
          }
        }
      }
    });

    try {
      // Mock data is now handled by loadMockDataSet() method 

      // Convert QueryFilter format to AnalyticsRequest filter format
      const requestFilters = allFilters.map(filter => ({
        filterId: filter.field,
        value: filter.value
      }));

      debugLog(`🌐 [DataSetManager] Making server API call for dataset: ${dataSet.id}`);
      debugLog(`🌐 [DataSetManager] Request filters:`, requestFilters);

      // Call the analytics service (should make server call to generated endpoint)
      // Note: Don't set a limit here - let the server return all data
      // The limit should be applied at visualization level, not dataset level
      const response = await this.analyticsService.processAnalytics({
        configId: 'dataset-' + dataSet.id,
        filters: requestFilters,
        metrics: [], // Will be determined by visualization
        // Explicitly set no limit for dataset loads (filter generation needs all data)
        limit: undefined
      });

      debugLog(`🌐 [DataSetManager] Server response:`, response);
      debugLog(`🌐 [DataSetManager] Server data sample (first 2 records):`, response.data?.slice(0, 2));
      debugLog(`🌐 [DataSetManager] Server data fields:`, response.data?.[0] ? Object.keys(response.data[0]) : 'No data');
      if (response.data?.[0]) {
        debugLog(`🌐 [DataSetManager] Server total_sales values:`, response.data.slice(0, 3).map(r => r.total_sales));
        debugLog(`🌐 [DataSetManager] Server revenue values:`, response.data.slice(0, 3).map(r => r.revenue));
      }

      return response.data || [];
    } catch (error) {
      console.error(`❌ [DataSetManager] Failed to load dataset ${dataSet.id}:`, error);
      return [];
    }
  }

  /**
   * Apply visualization-specific filters to dataset with historical data support
   * 
   * HISTORICAL DATA FEATURE:
   * Visualizations can set `requiresHistoricalData: true` to preserve data outside the selected date range.
   * This is essential for:
   * - KPIs calculating variance (current period vs. previous period)
   * - Year-over-Year (YoY) comparisons
   * - Period-over-period trend analysis
   * - Analytics tables showing historical context
   * 
   * Configuration:
   * - `requiresHistoricalData: true` - Enables historical data preservation
   * - `historicalDataMultiplier: number` - Controls range extension (default: 2)
   *   Example: multiplier=2 with Dec 1-7 selection → includes Nov 24-Dec 7 (14 days)
   * 
   * Performance:
   * - Intelligent caching prevents re-filtering the same data
   * - Pre-computed timestamps on data rows for faster date comparisons
   */
  private applyVisualizationFilters(
    data: AnalyticsDataPoint[],
    filters: Record<string, any>,
    visualization?: AnalyticsVisualization,
    currentTabId?: string,
    requiredFields?: string[]
  ): AnalyticsDataPoint[] {
    const filterStart = performance.now();

    console.log(`🔍 [FILTER DEBUG] ========== applyVisualizationFilters START ==========`);
    console.log(`🔍 [FILTER DEBUG] Visualization: ${visualization?.id} (${visualization?.type})`);
    console.log(`🔍 [FILTER DEBUG] Input records: ${data.length}`);
    console.log(`🔍 [FILTER DEBUG] Filters being applied:`, JSON.stringify(filters, null, 2).substring(0, 500));
    console.log(`🔍 [FILTER DEBUG] forceLiveData: ${this.forceLiveData}`);
    console.log(`🔍 [FILTER DEBUG] requiresHistoricalData: ${visualization?.requiresHistoricalData || false} (multiplier: ${visualization?.historicalDataMultiplier || 2}x)`);
    debugLog(`🔍 [DataSetManager] applyVisualizationFilters: Starting with ${data.length} records for ${visualization?.id}`);
    debugLog(`🔍 [DataSetManager] applyVisualizationFilters: filters =`, filters);

    // PERFORMANCE OPTIMIZATION: Check pre-filtered cache first
    const relevantDateFilters = this.getRelevantDateFilters(filters, visualization);
    const preFilterCacheKey = this.getPreFilterCacheKey(data, relevantDateFilters, visualization);

    let filteredData: AnalyticsDataPoint[];

    if (this.preFilteredCache.has(preFilterCacheKey)) {
      // Cache hit - skip date filtering entirely
      const cached = this.preFilteredCache.get(preFilterCacheKey)!;
      filteredData = cached.data;
      console.log(`⏱️ [PERF] Date filter CACHE HIT for ${visualization?.id} (hist: ${visualization?.requiresHistoricalData || false}) - skipping ${data.length} record scan`);
      console.log(`⏱️ [PERF] Date filter CACHE HIT for ${visualization?.id} (hist: ${visualization?.requiresHistoricalData || false}) - skipping ${data.length} record scan`);
    } else {
      // Cache miss - need to filter
      filteredData = [...data];

      // Debug: Show original date range
      if (data.length > 0) {
        const dates = data.map(d => new Date(d.date)).filter(d => !isNaN(d.getTime())).sort((a, b) => a.getTime() - b.getTime());
        debugLog(`🔍 [DataSetManager] applyVisualizationFilters: Date range in data: ${dates[0]?.toISOString()?.split('T')[0]} to ${dates[dates.length - 1]?.toISOString()?.split('T')[0]}`);
      }

      // Apply date range filtering - only apply relevant date filters
      debugLog(`🔍 [DataSetManager] applyVisualizationFilters: relevantDateFilters =`, relevantDateFilters);

      Object.entries(relevantDateFilters).forEach(([key, value]) => {
        if (value && typeof value === 'object' && ('start' in value || 'end' in value)) {
          const dateRange = value as any; // Type assertion for date range object
          debugLog(`🔍 [DataSetManager] applyVisualizationFilters: Applying date filter '${key}' with range:`, dateRange);

          const beforeCount = filteredData.length;
          const dateFilterStart = performance.now();

          // OPTIMIZED: Cached date parser to avoid re-parsing same dates
          // Uses UTC to ensure consistent behavior across timezones
          const parseLocalDate = (val: any, endOfDay: boolean = false): Date | null => {
            if (!val) return null;

            // Fast path for Date objects - use UTC methods
            if (val instanceof Date) {
              if (endOfDay) {
                return new Date(Date.UTC(val.getUTCFullYear(), val.getUTCMonth(), val.getUTCDate(), 23, 59, 59, 999));
              }
              return new Date(Date.UTC(val.getUTCFullYear(), val.getUTCMonth(), val.getUTCDate(), 0, 0, 0, 0));
            }

            // Cached path for string dates
            if (typeof val === 'string') {
              const cacheKey = `${val}::${endOfDay}`;

              // Check cache first
              if (this.dateParseCach.has(cacheKey)) {
                return this.dateParseCach.get(cacheKey)!;
              }

              // Parse and cache - use UTC to avoid timezone issues
              const s = val.includes('T') ? val.split('T')[0] : val;
              const parts = s.split('-').map(Number);
              let parsed: Date | null = null;

              if (parts.length === 3 && parts.every(n => !isNaN(n))) {
                const [y, m, d] = parts;
                // Use Date.UTC to create dates in UTC timezone
                parsed = endOfDay
                  ? new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999))
                  : new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
              } else {
                // Fallback to native parse
                const dte = new Date(val);
                parsed = !isNaN(dte.getTime()) ? dte : null;
              }

              // Cache the result
              if (parsed) {
                this.dateParseCach.set(cacheKey, parsed);
              }

              return parsed;
            }

            return null;
          };

          // OPTIMIZATION: Pre-compute filter dates once (not per row)
          const resolveDate = (dateValue: any): Date | null => {
            if (!dateValue) return null;
            if (dateValue instanceof Date) return parseLocalDate(dateValue);
            if (typeof dateValue === 'string') {
              if (DateConstantsService.isDateConstant(dateValue)) {
                const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
                return parseLocalDate(resolvedDateString);
              }
              return parseLocalDate(dateValue);
            }
            return null;
          };

          let startDate = resolveDate(dateRange.start);
          const endDateRaw = resolveDate(dateRange.end);
          let endDate = endDateRaw ? parseLocalDate(endDateRaw, true) : null;

          // 📊 HISTORICAL DATA SUPPORT: Extend date range for visualizations that need historical data
          // This is crucial for KPIs calculating variance, YoY, and other period-over-period comparisons
          if (visualization?.requiresHistoricalData && startDate && endDate) {
            const strategy = visualization.historicalDataStrategy || 'previous-period';
            const multiplier = visualization.historicalDataMultiplier || 2;
            const periodDurationMs = endDate.getTime() - startDate.getTime();

            let extendedStartDate = startDate;
            const dateRanges: string[] = [];

            switch (strategy) {
              case 'previous-period': {
                // Extend backwards by period duration (for variance: current vs. previous period)
                const extendedDurationMs = periodDurationMs * (multiplier - 1); // -1 because we already have 1x
                extendedStartDate = new Date(startDate.getTime() - extendedDurationMs);
                dateRanges.push(`${extendedStartDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
                break;
              }

              case 'year-over-year': {
                // Include same period from previous year(s) for YoY comparison
                // Calculate year-ago start and end dates
                const yearAgoStart = new Date(startDate);
                yearAgoStart.setFullYear(startDate.getFullYear() - 1);

                const yearAgoEnd = new Date(endDate);
                yearAgoEnd.setFullYear(endDate.getFullYear() - 1);

                // Use the earliest date (year-ago start) as the new start
                extendedStartDate = yearAgoStart;
                dateRanges.push(`${yearAgoStart.toISOString().split('T')[0]} to ${yearAgoEnd.toISOString().split('T')[0]} (YoY)`);
                dateRanges.push(`${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]} (Current)`);
                break;
              }

              case 'both': {
                // Include BOTH previous period AND year-ago period
                // Previous period
                const extendedDurationMs = periodDurationMs * (multiplier - 1);
                const previousPeriodStart = new Date(startDate.getTime() - extendedDurationMs);

                // Year-ago period
                const yearAgoStart = new Date(startDate);
                yearAgoStart.setFullYear(startDate.getFullYear() - 1);

                const yearAgoEnd = new Date(endDate);
                yearAgoEnd.setFullYear(endDate.getFullYear() - 1);

                // Use the earliest date between previous period and year-ago
                extendedStartDate = previousPeriodStart.getTime() < yearAgoStart.getTime()
                  ? previousPeriodStart
                  : yearAgoStart;

                dateRanges.push(`${previousPeriodStart.toISOString().split('T')[0]} to ${new Date(startDate.getTime() - 1).toISOString().split('T')[0]} (Previous)`);
                dateRanges.push(`${yearAgoStart.toISOString().split('T')[0]} to ${yearAgoEnd.toISOString().split('T')[0]} (YoY)`);
                dateRanges.push(`${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]} (Current)`);
                break;
              }
            }

            console.log(`📊 [HISTORICAL DATA] Extending date range for ${visualization.id}:`);
            console.log(`   Strategy: ${strategy}`);
            console.log(`   Original: ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
            console.log(`   Extended: ${extendedStartDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
            console.log(`   Date ranges included:`);
            dateRanges.forEach((range, i) => console.log(`     ${i + 1}. ${range}`));

            startDate = extendedStartDate;
          }

          debugLog(`🔍 [DataSetManager] Resolved dates: start=${startDate?.toISOString()?.split('T')[0]}, end=${endDate?.toISOString()?.split('T')[0]}`);

          // OPTIMIZATION: Convert to timestamps for faster comparison (numbers are faster than Date comparisons)
          const startTime = startDate ? startDate.getTime() : Number.NEGATIVE_INFINITY;
          const endTime = endDate ? endDate.getTime() : Number.POSITIVE_INFINITY;

          filteredData = filteredData.filter(row => {
            if (!row.date) return true;

            // FAST PATH: Use pre-computed timestamp if available
            let rowTime = (row as any)._timestamp;

            if (!rowTime) {
              // Fallback: Parse date on the fly
              const rowDate = parseLocalDate((row as any).date);
              if (!rowDate || isNaN(rowDate.getTime())) return true;
              rowTime = rowDate.getTime();
            }

            return rowTime >= startTime && rowTime <= endTime;
          });

          const dateFilterEnd = performance.now();
          const dateFilterTime = dateFilterEnd - dateFilterStart;

          console.log(`⏱️ [PERF] Date filter '${key}' complete: ${beforeCount} → ${filteredData.length} records in ${dateFilterTime.toFixed(2)}ms`);
          debugLog(`🔍 [DataSetManager] applyVisualizationFilters: Date filter '${key}' processed ${beforeCount} → ${filteredData.length} records in ${dateFilterTime.toFixed(2)}ms`);

        }
      });

      // Cache the date-filtered data for reuse by other visualizations
      this.preFilteredCache.set(preFilterCacheKey, {
        data: filteredData,
        filterKey: preFilterCacheKey
      });
    }

    // PERFORMANCE OPTIMIZATION: Apply non-date filters in the same pass as date filters if possible
    // This avoids creating intermediate arrays
    const nonDateFilters = this.getNonDateFilters(filters, visualization);
    const nonDateFilterStart = performance.now();

    if (this.forceLiveData) {
      // In live data mode, apply global filters (filters that start with "global-") to all visualizations
      // Also allow visualization-specific filters if explicitly configured with locationFilterId
      const allowedFilters: Record<string, any> = {};

      // 1. Add all global filters (filters with IDs starting with "global-")
      Object.entries(nonDateFilters).forEach(([filterKey, filterValue]) => {
        if (filterKey.startsWith('global-')) {
          allowedFilters[filterKey] = filterValue;
          debugLog(`🌐 [DataSetManager] Including global filter ${filterKey} in live data mode for ${visualization?.id}`);
        }
      });

      // 2. Check if visualization has locationFilterId in config and allow that filter
      const vizConfig = visualization?.config as any;
      if (vizConfig?.locationFilterId && nonDateFilters[vizConfig.locationFilterId]) {
        allowedFilters[vizConfig.locationFilterId] = nonDateFilters[vizConfig.locationFilterId];
        debugLog(`🔧 [DataSetManager] Allowing location filter ${vizConfig.locationFilterId} in live data mode for ${visualization?.id}`);
      }

      // If no allowed filters, skip all non-date filters
      if (Object.keys(allowedFilters).length === 0) {
        debugLog(`🔧 [DataSetManager] No global or visualization-specific filters to apply in live data mode for ${visualization?.id}`);
        debugLog(`✅ [DataSetManager] applyVisualizationFilters final: ${filteredData.length} records for ${visualization?.id}`);
        return filteredData;
      }

      debugLog(`🔧 [DataSetManager] Applying ${Object.keys(allowedFilters).length} filter(s) in live data mode for ${visualization?.id}`);

      // Process the allowed filters
      Object.entries(allowedFilters).forEach(([filterKey, filterValue]) => {
        const beforeCount = filteredData.length;

        // Apply the same filter processing logic as below
        if (Array.isArray(filterValue)) {
          // BUGFIX: Skip filtering if array is empty (no selections = show all)
          if (filterValue.length === 0) {
            debugLog(`🔧 [DataSetManager] Skipping filter ${filterKey} - no selections (empty array)`);
            return;
          }

          const filterValues = filterValue.map((item: any) => {
            if (typeof item === 'object' && item !== null) {
              if (item.value !== undefined) return item.value;
              if (item.id !== undefined) return item.id;
              if (item instanceof String || this.isStringLikeObject(item)) {
                return item.valueOf ? item.valueOf() : item.toString();
              }
              return item;
            } else {
              return item;
            }
          });

          let fieldName = this.getFieldNameFromFilterKey(filterKey);
          fieldName = this.getExplicitFieldMapping(filterKey, fieldName);

          if (fieldName) {
            filteredData = filteredData.filter(row => {
              const fieldValue = (row as any)[fieldName];
              return filterValues.includes(fieldValue);
            });

            debugLog(`🔧 [DataSetManager] Applied filter ${filterKey} → ${fieldName}: ${beforeCount} → ${filteredData.length} records`);
          }
        }
      });

      const nonDateFilterEnd = performance.now();
      console.log(`🔍 [FILTER DEBUG] Non-date filters complete: ${filteredData.length} records`);
      console.log(`🔍 [FILTER DEBUG] ========== applyVisualizationFilters END (live mode) ==========`);
      debugLog(`✅ [DataSetManager] applyVisualizationFilters final: ${filteredData.length} records for ${visualization?.id}`);
      return filteredData;
    }

    // Continue with normal mock data filtering logic
    Object.entries(nonDateFilters).forEach(([filterKey, filterValue]) => {
      const beforeCount = filteredData.length;

      // Check if this filter should apply to the current visualization based on tab context
      if (currentTabId && !this.shouldApplyFilterToTab(filterKey, currentTabId, visualization)) {
        debugLog(`[DataSetManager] 🚫 Skipping filter ${filterKey} for tab ${currentTabId} (tab-specific filter)`);
        return;
      }

      // Generic multi-select filter handling
      if (Array.isArray(filterValue)) {

        // Extract values from filter objects with detailed logging
        const filterValues = filterValue.map((item, index) => {

          let extractedValue;
          if (typeof item === 'object' && item !== null) {
            // Handle different possible object structures
            if (item.value !== undefined) {
              extractedValue = item.value;
            } else if (item.id !== undefined) {
              extractedValue = item.id;
            } else if (item instanceof String || this.isStringLikeObject(item)) {
              // Handle String objects (convert to primitive string)
              extractedValue = item.valueOf ? item.valueOf() : item.toString();
            } else {
              // If it's an object but no recognized property, use the whole object
              extractedValue = item;
            }
          } else {
            // Primitive value or null
            extractedValue = item;
          }

          return extractedValue;
        });


        // Apply filter to the corresponding field (convert filter key to field name)
        let fieldName = this.getFieldNameFromFilterKey(filterKey);

        // GENERIC: Override field name if filter config explicitly specifies one
        const newFieldName = this.getExplicitFieldMapping(filterKey, fieldName);
        fieldName = newFieldName;

        if (fieldName) {
          filteredData = filteredData.filter(row => {
            const fieldValue = (row as any)[fieldName];
            return filterValues.includes(fieldValue);
          });
        }
      }

      // Add generic multi-select filter handling here if needed
      // Domain-specific filter logic should be handled through configuration, not hardcoded

      // Add more non-date filter types here as needed
    });

    const nonDateFilterEnd = performance.now();
    console.log(`🔍 [FILTER DEBUG] Mock mode - After non-date filters: ${filteredData.length} records`);
    console.log(`⏱️ [PERF] Non-date filters complete for ${visualization?.id}: ${(nonDateFilterEnd - nonDateFilterStart).toFixed(2)}ms`);

    // Apply visualization-specific dataQuery.filters (e.g., for performance cards)
    // Skip for calculated performance cards as they're handled in SmartDataManager
    const skipDataQueryFilters = visualization?.type === 'performance-card' &&
      (visualization.config as any)?.mode === 'calculated';

    if (visualization?.dataQuery?.filters && Array.isArray(visualization.dataQuery.filters) && !skipDataQueryFilters) {
      const beforeCount = filteredData.length;

      visualization.dataQuery.filters.forEach(filter => {
        const { field, operator, value } = filter;

        filteredData = filteredData.filter(row => {
          const rowValue = (row as any)[field];

          switch (operator) {
            case 'eq':
              return rowValue === value;
            case 'ne':
              return rowValue !== value;
            case 'gt':
              return rowValue > value;
            case 'gte':
              return rowValue >= value;
            case 'lt':
              return rowValue < value;
            case 'lte':
              return rowValue <= value;
            case 'in':
              return Array.isArray(value) && value.includes(rowValue);
            case 'nin':
              return Array.isArray(value) && !value.includes(rowValue);
            default:
              console.warn(`⚠️ [DataSetManager] Unknown filter operator: ${operator}`);
              return true;
          }
        });
      });

    }

    // Apply specific field filter for performance cards (generic approach - deprecated)
    if (visualization?.type === 'performance-card') {
      const cardConfig = visualization.config as any;
      if (cardConfig.fieldFilter && cardConfig.fieldName && cardConfig.fieldValue) {
        const beforeCount = filteredData.length;
        const fieldName = cardConfig.fieldName;
        const fieldValue = cardConfig.fieldValue;

        filteredData = filteredData.filter(row => {
          return (row as any)[fieldName] === fieldValue;
        });
      }
      // Note: orderTypeFilter is now handled via dataQuery.filters in the visualization config
    }


    const filterEnd = performance.now();
    const totalFilterTime = filterEnd - filterStart;

    console.log(`⏱️ [PERF] Filter complete for ${visualization?.id}: ${data.length} → ${filteredData.length} records in ${totalFilterTime.toFixed(2)}ms`);

    // 🚀 FIELD-LEVEL OPTIMIZATION: Prune data to only required fields
    if (requiredFields && requiredFields.length > 0 && filteredData.length > 0) {
      const beforeFields = Object.keys(filteredData[0] || {}).length;
      filteredData = filteredData.map(row => {
        const prunedRow: any = {};
        requiredFields.forEach(field => {
          if (row.hasOwnProperty(field)) {
            prunedRow[field] = (row as any)[field];
          }
        });
        // Always preserve internal timestamp if it exists
        if ((row as any)._timestamp) prunedRow._timestamp = (row as any)._timestamp;
        return prunedRow;
      });
      const afterFields = Object.keys(filteredData[0] || {}).length;
      console.log(`🧹 [FIELD OPTIMIZATION] Pruned ${beforeFields} fields down to ${afterFields} for ${visualization?.id}`);
    }

    return filteredData;
  }

  /**
   * Check if a visualization should be visible based on conditional configuration
   */
  private shouldShowVisualization(visualization: AnalyticsVisualization, filters: Record<string, any>): boolean {
    const config = visualization.config as any;
    if (!config.conditional?.showWhen) {
      return true; // Show by default if no conditional logic
    }

    const { filterId, includes } = config.conditional.showWhen;
    const filterValue = filters[filterId];



    // Hide if filter not set
    if (filterValue === null || filterValue === undefined) {
      return false;
    }

    // Hide if filter is not an array
    if (!Array.isArray(filterValue)) {
      return false;
    }

    // Hide if filter is an empty array (nothing selected)
    if (filterValue.length === 0) {
      return false;
    }

    // Check if any of the required values are included in the filter
    const hasRequiredValues = includes.some((requiredValue: string) => filterValue.includes(requiredValue));


    return hasRequiredValues;
  }

  /**
   * Generate cache key for pre-filtered data
   */
  private getPreFilterCacheKey(data: AnalyticsDataPoint[], dateFilters: Record<string, any>, visualization?: AnalyticsVisualization): string {
    // Create a unique key based on data reference and filter values
    const dataKey = data.length > 0 ? `data-${data.length}-${data[0]?.date}` : 'empty';
    const filterKey = Object.entries(dateFilters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => {
        if (v && typeof v === 'object' && ('start' in v || 'end' in v)) {
          return `${k}:${v.start || ''}:${v.end || ''}`;
        }
        return `${k}:${v}`;
      })
      .join('|');

    // Include historical data flags in cache key to prevent cache collisions
    // KPIs with requiresHistoricalData need separate cache entries
    const historicalKey = visualization?.requiresHistoricalData
      ? `hist:${visualization.historicalDataMultiplier || 2}:${visualization.historicalDataStrategy || 'previous-period'}`
      : 'no-hist';

    return `${dataKey}::${filterKey}::${historicalKey}`;
  }

  /**
   * Get only the relevant date filters for a specific visualization
   */
  private getRelevantDateFilters(filters: Record<string, any>, visualization?: AnalyticsVisualization): Record<string, any> {
    // If no visualization provided, use all date filters (backward compatibility)
    if (!visualization) {
      const dateFilters: Record<string, any> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (key.includes('date')) {
          dateFilters[key] = value;
        }
      });
      return dateFilters;
    }

    // For calculated charts, performance cards, tables, and kpis, use the specific dateFilterId
    if ((visualization.type === 'chart' || visualization.type === 'performance-card' || visualization.type === 'table' || visualization.type === 'kpi') && (visualization.config as any)?.dateFilterId) {
      const dateFilterId = (visualization.config as any).dateFilterId;
      if (filters[dateFilterId]) {
        return { [dateFilterId]: filters[dateFilterId] };
      }
    }

    // For other visualizations, try to find a matching date filter by convention
    // Check for visualization-specific and generic date filters
    const possibleFilters = [
      `${visualization.id}-date-range`,  // Visualization-specific filter (e.g., order-guest-table-date-range)
      // Try tab-level date filter pattern (e.g., order-guest-date-range for order-guest-table)
      visualization.id.replace(/-(table|chart|kpi|card)$/, '-date-range'),
      'date-range',                      // Generic date range filter
      'main-date-range'                  // Alternative generic filter
    ];

    for (const filterId of possibleFilters) {
      if (filters[filterId]) {
        return { [filterId]: filters[filterId] };
      }
    }

    // Fallback: use the first date filter found
    const firstDateFilter = Object.entries(filters).find(([key]) => key.includes('date'));
    if (firstDateFilter) {
      debugLog(`🎯 [DataSetManager] Using fallback date filter '${firstDateFilter[0]}' for ${visualization.id}`);
      return { [firstDateFilter[0]]: firstDateFilter[1] };
    }

    return {};
  }

  /**
   * Generate cache key - optimized for both mock data and intelligent live data caching
   */
  private getCacheKey(dataSetId: string, filters: Record<string, any>): string {
    const dataSet = this.dataSets.get(dataSetId);

    // For mock data (when NOT in forceLiveData mode), only cache based on dataset ID 
    // (no filters) since filters don't affect generation
    // CRITICAL: Check forceLiveData to prevent cache key collisions between mock and live data
    if (dataSet?.mockDataConfig && !this.forceLiveData) {
      return `${dataSetId}::mock-base`;
    }

    // For live data mode, use a distinct cache key prefix to prevent mixing with mock data
    if (this.forceLiveData) {
      return `${dataSetId}::live-data`;
    }

    // For API data, the backend currently only considers date range parameters.
    // To avoid redundant server calls from multiple visualizations (e.g., KPI cards)
    // that apply client-side filters, we build the cache key ONLY from date filters.
    // This ensures a single fetch per date range, with client-side filtering afterward.
    const dateFilters: Array<{ start?: any; end?: any }> = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
        const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
        if (!hasStart && !hasEnd) return;
        const startVal = (value as any).start;
        const endVal = (value as any).end;
        const hasDateConstants = (typeof startVal === 'string' && startVal.length > 0) || (typeof endVal === 'string' && endVal.length > 0);
        const hasActualDates = (startVal instanceof Date) || (endVal instanceof Date);
        if (!hasDateConstants && !hasActualDates) return;
        dateFilters.push({ start: startVal, end: endVal });
      }
    });

    // Canonicalize date filters so different filter IDs (per tab) share the same cache key
    const meaningfulFilters: Record<string, any> = {};
    if (dateFilters.length > 0) {
      // If multiple, collapse to a single canonical entry; prefer the first
      const canonical = dateFilters[0];
      meaningfulFilters['date'] = { start: canonical.start, end: canonical.end };
    }

    // Generate cache key only from meaningful filters
    if (Object.keys(meaningfulFilters).length === 0) {
      return `${dataSetId}::no-filters`;
    }

    const filterKey = Object.entries(meaningfulFilters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${JSON.stringify(v)}`)
      .join('|');
    return `${dataSetId}::${filterKey}`;
  }



  /**
   * Check if cached data is still valid
   */
  private isCacheValid(cached: { timestamp: number; ttl: number }): boolean {
    return Date.now() - cached.timestamp < cached.ttl;
  }

  /**
   * Clear cache (useful for refresh operations)
   */
  clearCache(dataSetId?: string): void {
    if (dataSetId) {
      // Clear specific dataset cache
      const keysToDelete = Array.from(this.dataCache.keys()).filter(key => key.startsWith(dataSetId));
      keysToDelete.forEach(key => this.dataCache.delete(key));
    } else {
      // Clear all cache
      this.dataCache.clear();
    }

    // PERFORMANCE: Clear performance optimization caches too
    this.dateParseCach.clear();
    this.preFilteredCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { totalItems: number; totalSize: number; datasets: string[] } {
    const totalItems = this.dataCache.size;
    const datasets = Array.from(this.dataSets.keys());

    let totalSize = 0;
    this.dataCache.forEach(cached => {
      totalSize += cached.data.length;
    });

    return { totalItems, totalSize, datasets };
  }

  /**
   * Check if a filter should be applied to a specific tab
   * Tab-specific filters only apply to their own tab, dashboard-wide filters apply to all tabs
   */
  private shouldApplyFilterToTab(filterKey: string, currentTabId: string, visualization?: AnalyticsVisualization): boolean {
    // Get dashboard config from registered configs
    // For now, get the first registered dashboard config (assuming single dashboard per manager)
    const dashboardConfig = Array.from(this.dashboardConfigs.values())[0];

    if (!dashboardConfig?.tabs) {
      return true; // If no config, apply all filters (backward compatibility)
    }

    // Find which tabs define this filter first
    const tabsWithFilter: string[] = [];
    dashboardConfig.tabs.forEach((tab: any) => {
      tab.visualizations?.forEach((viz: any) => {
        if (viz.type === 'filter' && viz.config?.filterId === filterKey) {
          tabsWithFilter.push(tab.id);
        }
      });

      // Check sub-tabs too
      tab.subTabs?.forEach((subTab: any) => {
        subTab.visualizations?.forEach((viz: any) => {
          if (viz.type === 'filter' && viz.config?.filterId === filterKey) {
            tabsWithFilter.push(tab.id);
          }
        });
      });
    });

    // Check if current visualization explicitly needs this filter
    if (visualization) {
      const vizConfig = visualization.config as any;

      // If visualization explicitly references this filter AND it's a tab-specific filter,
      // only apply it if we're in the same tab where the filter is defined
      if (vizConfig?.locationFilterId === filterKey ||
        vizConfig?.dateFilterId === filterKey ||
        vizConfig?.filterId === filterKey) {

        // If it's a tab-specific filter (defined in only one tab),
        // only apply if current tab matches the filter's tab
        if (tabsWithFilter.length === 1) {
          return tabsWithFilter[0] === currentTabId;
        }

        // If it's a dashboard-wide filter, always apply
        return true;
      }
    }

    // If filter is defined in multiple tabs, it's dashboard-wide (apply to all tabs)
    if (tabsWithFilter.length > 1) {
      return true;
    }

    // If filter is defined in only one tab, it's tab-specific (only apply to that tab)
    if (tabsWithFilter.length === 1) {
      return tabsWithFilter[0] === currentTabId;
    }

    // If filter is not found in any tab, apply it (might be a global filter)
    return true;
  }

  /**
   * Get explicit field mapping from filter configuration
   */
  private getExplicitFieldMapping(filterKey: string, defaultFieldName: string | null): string | null {
    // Look through all registered dashboard configs for this filter's field mapping
    const dashboardConfig = Array.from(this.dashboardConfigs.values())[0];
    if (!dashboardConfig) {
      return defaultFieldName;
    }

    // Find the filter configuration that matches this filterKey
    let explicitField: string | null = null;

    // FIRST: Check global dashboard-level filters
    if (dashboardConfig.filters) {
      const globalFilter = dashboardConfig.filters.find((filter: any) => filter.filterId === filterKey);
      if (globalFilter && globalFilter.field) {
        explicitField = globalFilter.field;
      }
    }

    // SECOND: Check filter visualizations within tabs (if not found in global filters)
    if (!explicitField && dashboardConfig.tabs) {
      dashboardConfig.tabs.forEach((tab: any) => {
        tab.visualizations?.forEach((viz: any) => {
          if (viz.type === 'filter' && viz.config?.filterId === filterKey) {
            // Use the explicit field mapping from configuration
            if (viz.config.field) {
              explicitField = viz.config.field;
            }
          }
        });
      });
    }

    if (explicitField) {
      return explicitField;
    }

    return defaultFieldName;
  }

  /**
   * Convert filter key to corresponding field name in data records
   * Uses generic transformation rules to map filter IDs to data field names
   */
  private getFieldNameFromFilterKey(filterKey: string): string | null {
    // Generic transformation: convert kebab-case to snake_case
    const snakeCaseField = filterKey.replace(/-/g, '_');

    // Remove common filter suffixes to get the base field name
    const cleanField = snakeCaseField
      .replace(/_filter$/, '')
      .replace(/_selection$/, '')
      .replace(/_range$/, '')
      .replace(/_order_type$/, '') // Remove tab-specific suffixes
      .replace(/_sales_summary$/, '')
      .replace(/_register$/, '')
      .replace(/_zone$/, '')
      .replace(/_revenue_center$/, '');

    return cleanField;
  }

  /**
   * Get non-date filters that should be applied to the data
   */
  private getNonDateFilters(filters: Record<string, any>, visualization?: AnalyticsVisualization): Record<string, any> {
    const nonDateFilters: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      // Skip date filters
      if (key.includes('date')) {
        return;
      }


      // 🚀 FIX: Actually add the filter to nonDateFilters!
      if (Array.isArray(value) && value.length > 0) {
        nonDateFilters[key] = value;
      } else if (value !== null && value !== undefined) {
        nonDateFilters[key] = value;
      }
    });

    return nonDateFilters;
  }

  private isStringLikeObject(item: any): boolean {
    // Check if object has numeric string keys (0, 1, 2...) which indicates a String object
    if (typeof item !== 'object' || item === null) return false;

    // Check if it has valueOf or toString methods that return strings (common String object pattern)
    if (typeof item.valueOf === 'function' && typeof item.valueOf() === 'string') return true;
    if (typeof item.toString === 'function' && typeof item.toString() === 'string' && item.toString() !== '[object Object]') return true;

    const keys = Object.keys(item);
    if (keys.length === 0) return false;

    // Check if all keys are numeric strings and values are single characters
    return keys.every((key, index) =>
      key === index.toString() &&
      typeof item[key] === 'string' &&
      item[key].length === 1
    );
  }

  /**
   * Get any cached data for a dataset, regardless of filters
   * This allows filter generation to reuse already-loaded data without making another API call
   */
  public getAnyCachedDataForDataset(dataSetId: string): AnalyticsDataPoint[] | null {
    // Look for any cache entry that starts with this dataset ID
    for (const [key, cached] of this.dataCache.entries()) {
      if (key.startsWith(dataSetId) && this.isCacheValid(cached)) {
        console.log(`✅ [DataSetManager] Found cached data for ${dataSetId} (cache key: ${key}), ${cached.data.length} records`);
        return cached.data;
      }
    }
    return null;
  }

  /**
   * Generate dynamic filter options from the actual dataset
   * This is called when switching to live data to populate filters with actual values
   * 🔒 OPTIMIZATION: Uses already-cached data if available to avoid duplicate API calls
   */
  public async generateDynamicFilterOptions(
    dataSetId: string,
    fieldName: string,
    hierarchyConfig?: any
  ): Promise<Array<{ value: any; label: string; children?: any[] }>> {
    console.log(`🔧 [DataSetManager] Generating dynamic filter options for field: ${fieldName} from dataset: ${dataSetId}`);

    try {
      // Get the full unfiltered dataset
      const dataSetConfig = this.dataSets.get(dataSetId);
      if (!dataSetConfig) {
        console.warn(`⚠️ [DataSetManager] Dataset ${dataSetId} not found for dynamic filter generation`);
        return [];
      }

      // 🔒 OPTIMIZATION: First check if we have ANY cached data for this dataset
      // This prevents duplicate API calls when tab data was already loaded
      let data = this.getAnyCachedDataForDataset(dataSetId);

      if (!data) {
        // No cached data available - need to make API call
        console.log(`📥 [DataSetManager] No cached data found, loading dataset: ${dataSetId}`);
        data = await this.loadDataSet(dataSetId, {});
      } else {
        console.log(`⚡ [DataSetManager] Using cached data for filter generation (${data.length} records)`);
      }

      if (!data || data.length === 0) {
        console.warn(`⚠️ [DataSetManager] No data returned for dataset ${dataSetId}`);
        return [];
      }

      console.log(`✅ [DataSetManager] Loaded ${data.length} records for filter option generation`);

      // Log unique locations for debugging the location count issue
      const uniqueLocations = new Set(data.map(row => (row as any).location || (row as any).location_name || (row as any).LOCATION_NAME));
      console.log(`📍 [DataSetManager] FILTER GEN: ${uniqueLocations.size} unique locations found for filter options`);
      console.log(`📍 [DataSetManager] FILTER GEN: Field being extracted: ${fieldName}`);

      // If hierarchyConfig is provided, build hierarchical options
      if (hierarchyConfig?.levels && hierarchyConfig.levels.length > 0) {
        return this.buildHierarchicalOptionsFromData(data, hierarchyConfig);
      }

      // Otherwise, extract unique values for the field
      const uniqueValues = new Set<string>();
      data.forEach(row => {
        const value = (row as any)[fieldName];
        if (value !== null && value !== undefined && value !== '') {
          uniqueValues.add(String(value));
        }
      });

      // Convert to filter options and sort
      const options = Array.from(uniqueValues)
        .sort()
        .map(value => ({
          value,
          label: value
        }));

      console.log(`✅ [DataSetManager] Generated ${options.length} unique filter options for ${fieldName}`);

      return options;

    } catch (error) {
      console.error(`❌ [DataSetManager] Error generating dynamic filter options:`, error);
      return [];
    }
  }

  /**
   * Build hierarchical filter options from data based on hierarchy configuration
   */
  private buildHierarchicalOptionsFromData(
    data: AnalyticsDataPoint[],
    hierarchyConfig: any
  ): Array<{ value: any; label: string; children?: any[] }> {
    const { levels, relationships } = hierarchyConfig;

    if (!levels || levels.length === 0) {
      return [];
    }

    // If relationships are explicitly defined, use those
    if (relationships) {
      return this.buildOptionsFromRelationships(relationships, levels);
    }

    // Otherwise, build from data by extracting unique combinations
    const hierarchyMap = new Map<string, Set<string>>();

    // Build parent-child relationships from data
    data.forEach(row => {
      for (let i = 0; i < levels.length - 1; i++) {
        const parentLevel = levels[i];
        const childLevel = levels[i + 1];
        const parentValue = (row as any)[parentLevel];
        const childValue = (row as any)[childLevel];

        if (parentValue && childValue) {
          const key = `${parentLevel}:${parentValue}`;
          if (!hierarchyMap.has(key)) {
            hierarchyMap.set(key, new Set());
          }
          hierarchyMap.get(key)!.add(childValue);
        }
      }
    });

    // Build top-level options (first level in hierarchy)
    const topLevel = levels[0];
    const topLevelValues = new Set<string>();
    data.forEach(row => {
      const value = (row as any)[topLevel];
      if (value) topLevelValues.add(String(value));
    });

    const options: Array<{ value: any; label: string; children?: any[] }> = [];

    topLevelValues.forEach(parentValue => {
      const option: any = {
        value: parentValue,
        label: parentValue
      };

      // Get children for this parent
      const childrenKey = `${topLevel}:${parentValue}`;
      const childrenSet = hierarchyMap.get(childrenKey);

      if (childrenSet && childrenSet.size > 0) {
        option.children = Array.from(childrenSet)
          .sort()
          .map(childValue => ({
            value: childValue,
            label: childValue,
            parentValue
          }));
      }

      options.push(option);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Build hierarchical options from explicit relationships configuration
   */
  private buildOptionsFromRelationships(
    relationships: Record<string, any>,
    levels: string[]
  ): Array<{ value: any; label: string; children?: any[] }> {
    // This is for explicitly defined hierarchies in the config
    // The relationships object structure depends on the hierarchy configuration
    const options: Array<{ value: any; label: string; children?: any[] }> = [];

    Object.entries(relationships).forEach(([parentValue, config]) => {
      const option: any = {
        value: parentValue,
        label: parentValue
      };

      // Check if this parent has children defined
      const childrenKey = levels[1]; // Second level in hierarchy
      if (config[childrenKey] && Array.isArray(config[childrenKey])) {
        option.children = config[childrenKey].map((childValue: string) => ({
          value: childValue,
          label: childValue,
          parentValue
        }));
      }

      options.push(option);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

} 